import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Box, Typography, Divider, Card, useMediaQuery, Skeleton } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { useQuery } from 'react-query'

// Local Imports
import AdxDrawerComponent from 'components/reusable/drawer/AdxDrawerComponent'
import AdxAvatarSide from 'module/leave/pages/adminLeaveSetting/components/AdxAvatarSide'

// API services
import { getLeavePolicies } from 'services/api/modules/leave/leavePolicy'
import AdxButton from 'components/custom/AdxButton'

const ManageLeaveCredit = () => {
  const [drawerState, setDrawerState] = useState({ right: true })
  const { data, isLoading } = useQuery(['leave-policy', { type: 3 }], async () => {
    const { data: apiData } = await getLeavePolicies()
    return apiData.data.leave_credit[0]
  })

  const approvalRoute = data?.approval_route[0] || {}
  const { name } = approvalRoute
  const routes = [
    approvalRoute.layer_one,
    approvalRoute.layer_two,
    approvalRoute.layer_three,
    approvalRoute.layer_four,
    approvalRoute.layer_five,
  ].filter(Boolean)

  const handleDrawer = (anchor, open) => (event) => {
    setDrawerState({ ...drawerState, [anchor]: open })
    navigate(-1)
  }

  let navigate = useNavigate()
  let theme = useTheme()
  const matches = useMediaQuery(theme.breakpoints.up('sm'))

  const LeaveCreditHeader = () => {
    return (
      <Box p={4}>
        <Typography variant="subtitle1">Leave Credit Request Policy</Typography>
      </Box>
    )
  }

  return (
    <AdxDrawerComponent
      sxIcon={{ marginRight: 4 }}
      sx={{ p: 0 }}
      state={drawerState}
      anchor="right"
      onClose={handleDrawer}
      headerprops={<LeaveCreditHeader />}
    >
      <Divider light />
      <Box
        display={'flex'}
        flexDirection={'column'}
        p={2}
        mt={2}
        rowGap={4}
        sx={{
          minHeight: 'calc(100vh - 120px)',
        }}
      >
        <Box display={'flex'} flex={1} flexDirection={'column'} sx={{ rowGap: 2.5, flex: 2 }}>
          <Box px={2} display={'flex'} flexDirection={'row'}>
            <Box
              sx={{
                width: { xs: 1, sm: 1 / 4, lg: 1 / 4 },
              }}
            >
              <Typography color="light-gray-text.main" variant="subtitle1">
                Documentation
              </Typography>
            </Box>

            <Box
              sx={{
                width: { xs: 1, sm: 1 / 3, lg: 1 / 3 },
              }}
            >
              {isLoading ? (
                <Skeleton width={'100%'} />
              ) : data.attachment_required === 1 ? (
                <Typography variant="subtitle1" color={'alternate.main'}>
                  Required
                </Typography>
              ) : (
                <Typography variant="subtitle1" color={'dark-gray-text.main'}>
                  Not Required
                </Typography>
              )}
            </Box>
          </Box>
          <Box px={2} display={'flex'} flexDirection={'row'}>
            <Box
              sx={{
                width: { xs: 1, sm: 1 / 4, lg: 1 / 4 },
              }}
            >
              <Typography color="light-gray-text.main" variant="subtitle1">
                Reasoning
              </Typography>
            </Box>

            <Box
              sx={{
                width: { xs: 1, sm: 1 / 3, lg: 1 / 3 },
              }}
            >
              {isLoading ? (
                <Skeleton width={'100%'} />
              ) : data.reason_required === 1 ? (
                <Typography variant="subtitle1" color={'alternate.main'}>
                  Required
                </Typography>
              ) : (
                <Typography variant="subtitle1" color={'dark-gray-text.main'}>
                  Not Required
                </Typography>
              )}
            </Box>
          </Box>
          <Box px={2} mb={2} display={'flex'} flexDirection={'row'}>
            <Box
              sx={{
                width: { xs: 1, sm: 1 / 4, lg: 1 / 4 },
              }}
            >
              <Typography color="light-gray-text.main" variant="subtitle1">
                Credit Expiry Period
              </Typography>
            </Box>
            <Box
              sx={{
                width: { xs: 1, sm: 1 / 3, lg: 1 / 3 },
              }}
            >
              {isLoading ? (
                <Skeleton width={'100%'} />
              ) : (
                <Typography color="dark-gray-text.main" variant="subtitle1">
                  {data?.credit_expiry_period_name
                    ? data?.credit_expiry_period_name !== 'Cycle Period' &&
                      data?.credit_expiry_period_name !== 'No Expiry Date'
                      ? `${data?.credit_expiry_amount} ${data?.credit_expiry_period_name}${
                          data?.credit_expiry_amount !== 1 ? 's' : ''
                        }`
                      : data?.credit_expiry_period_name
                    : 'Not Set'}
                </Typography>
              )}
            </Box>
          </Box>
          <Box
            mb={1}
            sx={{
              backgroundColor: 'gray-background.main',
              width: '100%',
              py: 0.5,
              px: 1.75,
              borderRadius: 1,
            }}
          >
            <Typography variant="subtitle1" color="light-gray-text.main">
              Approval
            </Typography>
          </Box>
          <Box px={2} mb={2} display={'flex'} flexDirection={'row'}>
            <Box
              sx={{
                width: { xs: 1, sm: 1 / 4, lg: 1 / 4 },
              }}
            >
              <Typography color="light-gray-text.main" variant="subtitle1">
                Approval Flow
              </Typography>
            </Box>
            <Box
              sx={{
                width: { xs: 1, sm: 1 / 3, lg: 1 / 3 },
              }}
            >
              {isLoading ? (
                <Skeleton width={'100%'} />
              ) : (
                <Typography color="dark-gray-text.main" variant="subtitle1">
                  {name}
                </Typography>
              )}
            </Box>
          </Box>
          <Box px={2} display={'flex'} flexDirection={'row'}>
            <Box
              sx={{
                width: { xs: 1, sm: 1 / 4, lg: 1 / 4 },
              }}
            >
              <Typography color="light-gray-text.main" variant="subtitle1">
                Flow:
              </Typography>
            </Box>
          </Box>
          <Box px={2}>
            {isLoading ? (
              <Skeleton width={'100%'} height={200} />
            ) : (
              <Card variant="outlined">
                {routes.map((route, index, arr) => {
                  return (
                    <React.Fragment key={index}>
                      <Box p={2} display={'flex'} alignItems={'center'} columnGap={2}>
                        <Typography
                          minWidth={82}
                          flexShrink={0}
                          variant="body1"
                          color="light-gray-text.main"
                        >
                          Step {index + 1}
                        </Typography>
                        {matches ? (
                          <Box width={110} flexShrink={0}>
                            <AdxAvatarSide
                              tooltipMax={4}
                              active
                              data={route.members}
                              imageData={route.members}
                            />
                          </Box>
                        ) : null}

                        <Box display={'flex'} flexDirection={'column'} minWidth={0}>
                          <Typography noWrap variant="body1" color="dark-gray-text.main">
                            {route.name}
                          </Typography>
                          <Typography noWrap variant="subtitle2" color="light-gray-text.main">
                            {route?.members?.map((member, index, arr) => {
                              if (index === arr.length - 1) {
                                return `${member.name}`
                              }
                              return `${member.name}, `
                            })}
                          </Typography>
                        </Box>
                      </Box>

                      {index < arr.length - 1 && <Divider variant="full" />}
                    </React.Fragment>
                  )
                })}
              </Card>
            )}
          </Box>
        </Box>
        <Box display={'flex'} px={2} alignItems={'center'}>
          <AdxButton
            variant={'outlined'}
            onClick={() => {
              navigate('/app/management-centre/company-management/leave/edit-leave-credit')
            }}
            sx={{ width: '100%' }}
          >
            Edit Leave Credit
          </AdxButton>
        </Box>
      </Box>
    </AdxDrawerComponent>
  )
}

export default ManageLeaveCredit
